import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { EventWebsiteState } from '@graphql/generated-base';
import { hasGraphQLErrorCode } from '@shared/utils/checkGraphqlError';
import { addError } from '@shared/utils/logger';
import type { WebsiteV2SiteSummary } from './useWebsiteV2Sites';
import type { WebsiteV2PublishError, WebsiteV2PublishResult } from './websiteV2Publish.types';
import {
  useWebsiteV2PublishSiteMutation,
  useWebsiteV2SyncEventWebsiteStateMutation,
  useWebsiteV2UnpublishSiteMutation,
  WebsiteV2EventWebsiteStateDocument,
} from './websiteV2Publish.graphql.generated';
import type { WebsiteV2EventWebsiteStateQuery, WebsiteV2EventWebsiteStateQueryVariables } from './websiteV2Publish.graphql.generated';

/** carbon-website-service publish refusals (`extensions.code`) — doc/website-v2/publish-paywall.md. */
export const PUBLISH_REQUIRES_PURCHASE_CODE = 'PUBLISH_REQUIRES_PURCHASE';
export const PURCHASE_CHECK_UNAVAILABLE_CODE = 'PURCHASE_CHECK_UNAVAILABLE';

const classifyPublishError = (error: unknown): WebsiteV2PublishError => {
  if (hasGraphQLErrorCode(error, PUBLISH_REQUIRES_PURCHASE_CODE)) return 'requiresPurchase';
  if (hasGraphQLErrorCode(error, PURCHASE_CHECK_UNAVAILABLE_CODE)) return 'purchaseCheckUnavailable';
  return 'unknown';
};

type UseWebsiteV2SitePublishingParams = Readonly<{
  eventId: string;
  /** The rendered list: the fallback when a fresh read fails. */
  sites: readonly WebsiteV2SiteSummary[];
  /** Resolves the FRESH site list (`useWebsiteV2Sites().refetchSites`). */
  refetchSites: () => Promise<readonly WebsiteV2SiteSummary[]>;
}>;

/*
 * ONE LIVE SITE PER EVENT, AND THE EVENT'S PUBLISH STATE FOLLOWS IT (decision 11 —
 * doc/website-v2/PLAN.md).
 *
 * Publish enforces one live site per event here (publish this site, then unpublish every sibling
 * still live — read from a FRESH site list, so a site published from another tab is demoted too)
 * until the service enforces it. Publish-then-demote (not the reverse) so a failure can never leave
 * the event dark: the worst case is two live sites, and guest resolution + `useWebsiteV2Sites` both
 * pick the most recently published, i.e. this one. That case is surfaced as `siblingStillLive`.
 *
 * The carbon site is the truth for a WebsiteV2 event; the legacy `Event.websiteState` mirrors it,
 * because event_service and everything that hasn't learned about WebsiteV2 still reads it (Joy
 * search's unpublished filter, the wedding publish side effects, `websiteFirstPublishedAt`). The
 * mirror is written here, after a publish lands and after an unpublish leaves no site live. It's a
 * joy-web stopgap: a publish that doesn't go through joy-web (the draft guest view's banner, the
 * editor iframe's own publish) isn't mirrored until carbon-website-service tells event_service
 * itself. A failed mirror write is logged, never reported as a failed publish: guests already see
 * the carbon state.
 */
export const useWebsiteV2SitePublishing = ({ eventId, sites, refetchSites }: UseWebsiteV2SitePublishingParams) => {
  const apolloClient = useApolloClient();
  const [publishSiteMutation, { loading: isPublishing }] = useWebsiteV2PublishSiteMutation();
  const [unpublishSiteMutation, { loading: isUnpublishing }] = useWebsiteV2UnpublishSiteMutation();
  const [syncEventWebsiteStateMutation] = useWebsiteV2SyncEventWebsiteStateMutation();

  /** The event's `websiteState` from the server, not the cache: another tab may have changed it. */
  const isEventPublished = useCallback(async () => {
    try {
      const { data } = await apolloClient.query<WebsiteV2EventWebsiteStateQuery, WebsiteV2EventWebsiteStateQueryVariables>({
        query: WebsiteV2EventWebsiteStateDocument,
        variables: { eventId },
        fetchPolicy: 'network-only',
      });
      return data.eventById?.websiteState === EventWebsiteState.published;
    } catch (error) {
      // Unknown: write it. A repeated publish beats an event left reading draft.
      addError(error, { context: 'useWebsiteV2SitePublishing#isEventPublished', eventId });
      return false;
    }
  }, [apolloClient, eventId]);

  /** The FRESH site list, or `fallback` when it can't be read (each caller says what that means). */
  const refetchSitesOr = useCallback(
    async <T>(fallback: T): Promise<readonly WebsiteV2SiteSummary[] | T> => {
      try {
        return await refetchSites();
      } catch (error) {
        addError(error, { context: 'useWebsiteV2SitePublishing#refetchSites', eventId });
        return fallback;
      }
    },
    [refetchSites, eventId],
  );

  /** Mirrors "the event has a live site" onto `websiteState`. Resolves whether it now matches. */
  const syncEventWebsiteState = useCallback(
    async (hasLiveSite: boolean) => {
      const websiteState = hasLiveSite ? EventWebsiteState.published : EventWebsiteState.draft;
      // Only a `published` the server already holds is skipped: writing it again would re-run
      // event_service's publish side effects for nothing. `draft` is always written; it has none, and
      // a skipped draft is how V1 content would stay public.
      if (hasLiveSite && (await isEventPublished())) return true;
      try {
        await syncEventWebsiteStateMutation({ variables: { payload: { eventId, websiteState } } });
        return true;
      } catch (error) {
        addError(error, { context: 'useWebsiteV2SitePublishing#syncEventWebsiteState', eventId, websiteState });
        return false;
      }
    },
    [eventId, isEventPublished, syncEventWebsiteStateMutation],
  );

  /** Makes `siteId` THE live site for the event, then mirrors that onto the event. */
  const publishSite = useCallback(
    async (siteId: string): Promise<WebsiteV2PublishResult> => {
      try {
        // `eventId` rides on every event-touching mutation (gateway cache-invalidation contract);
        // the service 404s on a mismatch, so it doubles as a guard against a foreign site id.
        await publishSiteMutation({ variables: { siteId, eventId } });
      } catch (error) {
        const classified = classifyPublishError(error);
        if (classified === 'unknown') addError(error, { context: 'useWebsiteV2SitePublishing#publishSite', eventId, siteId });
        return { error: classified };
      }
      // One live site per event: demote every OTHER live site, from a fresh list (a stale render
      // would miss a sibling published elsewhere since this page loaded).
      const fresh = await refetchSitesOr(sites);
      const stillLive = fresh.filter(other => other.id !== siteId && other.isPublished);
      const demotions = await Promise.allSettled(stillLive.map(other => unpublishSiteMutation({ variables: { siteId: other.id, eventId } })));
      demotions.forEach((outcome, index) => {
        if (outcome.status === 'rejected') {
          addError(outcome.reason, { context: 'useWebsiteV2SitePublishing#demoteSibling', eventId, siteId, siblingSiteId: stillLive[index].id });
        }
      });
      await syncEventWebsiteState(true);
      // Settles the UI; the publish landed either way.
      await refetchSitesOr(undefined);
      return demotions.some(outcome => outcome.status === 'rejected') ? { error: 'siblingStillLive' } : {};
    },
    [eventId, sites, publishSiteMutation, unpublishSiteMutation, refetchSitesOr, syncEventWebsiteState],
  );

  /** Takes `siteId` down. Throws when the unpublish fails; the event goes to draft once no site is live. */
  const unpublishSite = useCallback(
    async (siteId: string) => {
      await unpublishSiteMutation({ variables: { siteId, eventId } });
      // Unknown when the read fails: leave the mirror alone rather than guess.
      const fresh = await refetchSitesOr(null);
      if (fresh && !fresh.some(site => site.isPublished)) {
        await syncEventWebsiteState(false);
      }
    },
    [eventId, unpublishSiteMutation, refetchSitesOr, syncEventWebsiteState],
  );

  return { publishSite, unpublishSite, syncEventWebsiteState, isPublishing, isUnpublishing };
};
